import { notification } from '../../components';
import { ErrorMsg } from '../../settings/models/Error';

interface PydanticError {
  loc?: (string | number)[];
  msg?: string;
  type?: string;
}

const DUPLICATE_MESSAGE = 'A threshold for this pest/crop/country/level already exists.';

// Matches the backend quirk where a same-level duplicate trips the range check
// (422) before the 409, e.g. "overlap between 'low' and 'low'".
const SAME_LEVEL_OVERLAP = /overlap between '(\w+)' and '(\w+)'/i;

const isSameLevelOverlap = (detail: string): boolean => {
  const match = SAME_LEVEL_OVERLAP.exec(detail);
  return !!match && match[1] === match[2];
};

/**
 * Resolves the exact backend error message for a threshold CRUD failure and
 * shows it as an error toast. Handles both `detail` shapes:
 *  - string  -> business rules (overlap, min>=max, not found)
 *  - array   -> pydantic field validation ([].msg)
 * Also normalizes the 409 duplicate (and the 422 same-level-overlap quirk).
 */
export const notifyThresholdError = (error: ErrorMsg, fallbackMsg: string): string => {
  const status = error?.status;
  const detail = error?.response?.data?.detail;

  let message = fallbackMsg;

  if (status === 409) {
    message = DUPLICATE_MESSAGE;
  } else if (typeof detail === 'string') {
    message = isSameLevelOverlap(detail) ? DUPLICATE_MESSAGE : detail;
  } else if (Array.isArray(detail)) {
    message =
      (detail as PydanticError[])
        .map(item => item.msg)
        .filter(Boolean)
        .join('; ') || fallbackMsg;
  }

  notification('error', message);
  return message;
};
